import { BIFROST_ENABLED_LOCALES } from '@99designs/design-services-common';
import { SupportedLocale, __image, __url, asNinetyNineAbsUrl } from '@99designs/i18n';
import { buildCloudinaryURL } from '../components/redesignComponents/CloudinaryImage';
import { designProducts } from './products';
import { translatedTitle } from './translatedTitle';
import {
    DesignProduct,
    DesignProductPresenter,
    ProductImageOptions,
    ProductKey,
    isCloudinaryImage,
    isExternalImage,
} from './types';

export const NewDesignProductPresenter = (designProduct: DesignProduct) => {
    return {
        ...designProduct,
        isAvailableInLocale(locale: SupportedLocale) {
            return designProduct.availableLocales.includes(locale);
        },
        ctaUrl(locale: SupportedLocale, crossPlatformTracking?: URLSearchParams) {
            switch (designProduct.type) {
                case 'vista':
                    return buildHref(
                        designProduct.urls.getStarted.replace('{locale}', locale),
                        crossPlatformTracking
                    );
                case 'bifrost':
                    return BIFROST_ENABLED_LOCALES.includes(locale)
                        ? buildHref(__url(designProduct.urls.productDetails), crossPlatformTracking)
                        : buildHref(
                              __url(designProduct.urls.nnCategoryPage),
                              crossPlatformTracking
                          );
                case 'nn':
                    return buildHref(
                        asNinetyNineAbsUrl(designProduct.urls.nnCategoryPage),
                        crossPlatformTracking
                    );
            }
        },
        translatedTitle() {
            return translatedTitle(designProduct);
        },
        previewImage(options?: ProductImageOptions) {
            if (isCloudinaryImage(designProduct.images.preview)) {
                return {
                    url: buildCloudinaryURL(
                        __image(designProduct.images.preview.cloudinaryImagePath),
                        options?.cloudinaryOptions
                    ),
                    aspectRatio: designProduct.images.preview.aspectRatio,
                };
            } else if (isExternalImage(designProduct.images.preview)) {
                return {
                    url: __image(designProduct.images.preview.url),
                    aspectRatio: designProduct.images.preview.aspectRatio,
                };
            }
            return { url: '', aspectRatio: 0 };
        },
        searchPreviewImage: function (options?: ProductImageOptions) {
            if (isCloudinaryImage(designProduct.images.searchPreview)) {
                return {
                    url: buildCloudinaryURL(
                        __image(designProduct.images.searchPreview.cloudinaryImagePath),
                        options?.cloudinaryOptions
                    ),
                    aspectRatio: designProduct.images.searchPreview.aspectRatio,
                };
            } else if (isExternalImage(designProduct.images.searchPreview)) {
                return {
                    url: __image(designProduct.images.searchPreview.url),
                    aspectRatio: designProduct.images.searchPreview.aspectRatio,
                };
            }

            return { url: '', aspectRatio: 0 };
        },
    } as DesignProductPresenter;
};

export const getDesignProductPresenters = (
    locale: SupportedLocale
): Record<ProductKey, DesignProductPresenter> => {
    return Object.values(designProducts).reduce(
        (obj: Record<string, DesignProductPresenter>, productConfig) => {
            const product = NewDesignProductPresenter(productConfig);
            if (!product.isAvailableInLocale(locale)) {
                return obj;
            }
            obj[product.key] = product;
            return obj;
        },
        {}
    );
};

const buildHref = (ctaLink: string, crossPlatformTracking?: URLSearchParams) => {
    if (!crossPlatformTracking) {
        return ctaLink;
    }

    const url = new URL(ctaLink, 'https://vistaprint.com');
    const newCtaLink = ctaLink.replace(url.search, '');

    crossPlatformTracking.forEach((value, key) => {
        url.searchParams.set(key, value);
    });

    return newCtaLink + url.search;
};
